import React, { useRef, useState } from 'react';
import { Share2, Download, Check, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { toast } from 'sonner';

const backgroundStyles = [
  { 
    name: 'Burgundy Rose', 
    bg: 'bg-gradient-to-br from-sage to-sage-dark', 
    text: 'text-white',
    accent: 'text-white/80'
  },
  { 
    name: 'Golden Light', 
    bg: 'bg-gradient-to-br from-gold-light to-gold', 
    text: 'text-charcoal',
    accent: 'text-charcoal/70'
  },
  { 
    name: 'Soft Blush', 
    bg: 'bg-gradient-to-br from-sky-light to-sky', 
    text: 'text-charcoal',
    accent: 'text-charcoal/70'
  },
  { 
    name: 'Warm Cream', 
    bg: 'bg-gradient-to-br from-cream to-cream-paper', 
    text: 'text-charcoal',
    accent: 'text-sage'
  },
];

export const ShareableQuote = ({ quote, type = 'affirmation', reference = '' }) => {
  const [selectedStyle, setSelectedStyle] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const canvasRef = useRef(null);
  const cardRef = useRef(null);

  const style = backgroundStyles[selectedStyle];

  const downloadImage = async () => {
    try {
      const card = cardRef.current;
      if (!card) return;

      // Use html2canvas approach with canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size (Instagram story size)
      canvas.width = 1080;
      canvas.height = 1920;

      // Get background colors from style
      const bgColors = {
        'Burgundy Rose': ['#7B1045', '#5C0C33'],
        'Golden Light': ['#F3E5AB', '#D4AF37'],
        'Soft Blush': ['#F0DFE7', '#D4A0B5'],
        'Warm Cream': ['#FEFCFD', '#FBF5F7'],
      };

      const colors = bgColors[style.name] || ['#7B1045', '#5C0C33'];
      
      // Create gradient background
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, colors[0]);
      gradient.addColorStop(1, colors[1]);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Set text color
      const textColor = style.name === 'Burgundy Rose' ? '#FFFFFF' : '#2D2030';
      const accentColor = style.name === 'Burgundy Rose' ? 'rgba(255,255,255,0.8)' : '#7B1045';

      // Draw decorative quote marks
      ctx.font = 'bold 200px Georgia';
      ctx.fillStyle = style.name === 'Burgundy Rose' ? 'rgba(255,255,255,0.15)' : 'rgba(123,16,69,0.15)';
      ctx.fillText('"', 80, 350);

      // Draw type label
      ctx.font = '600 32px Nunito, sans-serif';
      ctx.fillStyle = accentColor;
      ctx.textAlign = 'center';
      ctx.fillText(type.toUpperCase(), canvas.width / 2, 500);

      // Draw main quote text with word wrap
      ctx.font = 'italic 52px Georgia, serif';
      ctx.fillStyle = textColor;
      
      const maxWidth = canvas.width - 160;
      const lineHeight = 72;
      const words = quote.split(' ');
      let line = '';
      let y = 700;
      const lines = [];

      for (let word of words) {
        const testLine = line + word + ' ';
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && line !== '') {
          lines.push(line.trim());
          line = word + ' ';
        } else {
          line = testLine;
        }
      }
      lines.push(line.trim());

      // Center the text block vertically
      const totalHeight = lines.length * lineHeight;
      y = (canvas.height - totalHeight) / 2 + 100;

      lines.forEach((l, i) => {
        ctx.fillText(`"${i === 0 ? '' : ''}${l}${i === lines.length - 1 ? '"' : ''}`, canvas.width / 2, y + i * lineHeight);
      });

      // Draw reference if exists
      if (reference) {
        ctx.font = '500 36px Nunito, sans-serif';
        ctx.fillStyle = accentColor;
        ctx.fillText(`— ${reference}`, canvas.width / 2, y + lines.length * lineHeight + 80);
      }

      // Draw branding
      ctx.font = '500 28px Caveat, cursive';
      ctx.fillStyle = accentColor;
      ctx.fillText('Breakthrough', canvas.width / 2, canvas.height - 120);

      // Download
      const link = document.createElement('a');
      link.download = `breakthrough-${type}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      toast.success('Quote image downloaded!', {
        description: 'Share it on your favorite social media',
      });
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error('Could not generate image');
    }
  };

  const copyToClipboard = async () => {
    const text = reference 
      ? `"${quote}" — ${reference}\n\n#Breakthrough #FaithHealing #HopeInChrist`
      : `"${quote}"\n\n#Breakthrough #FaithHealing #HopeInChrist`;
    
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Quote copied to clipboard!');
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Quote copied to clipboard!');
      } catch (e) {
        toast.error('Could not copy to clipboard');
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="share-quote-btn"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm text-sage hover:text-sage-dark hover:bg-sage/10 rounded-full transition-colors"
        >
          <Share2 className="w-4 h-4" />
          Share
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Share This {type === 'affirmation' ? 'Affirmation' : type === 'scripture' ? 'Scripture' : 'Quote'}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Preview Card */}
          <div 
            ref={cardRef}
            className={`${style.bg} ${style.text} rounded-xl p-8 aspect-[9/16] max-h-80 flex flex-col items-center justify-center text-center relative overflow-hidden`}
          >
            <div className="absolute top-6 left-6 text-6xl opacity-20 font-serif">"</div>
            <span className={`text-xs uppercase tracking-widest mb-4 ${style.accent}`}>
              {type}
            </span>
            <p className="font-heading text-xl italic leading-relaxed px-4">
              "{quote}"
            </p>
            {reference && (
              <p className={`mt-4 text-sm ${style.accent}`}>— {reference}</p>
            )}
            <p className={`absolute bottom-4 font-accent text-lg ${style.accent}`}>
              Breakthrough
            </p>
          </div>

          {/* Style Selector */}
          <div className="flex gap-2 justify-center">
            {backgroundStyles.map((s, idx) => (
              <button
                key={s.name}
                onClick={() => setSelectedStyle(idx)}
                data-testid={`style-${idx}`}
                className={`w-10 h-10 rounded-full ${s.bg} border-2 transition-all ${
                  selectedStyle === idx ? 'border-charcoal scale-110' : 'border-transparent'
                }`}
                title={s.name}
              />
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-center pt-2">
            <button
              onClick={downloadImage}
              data-testid="download-quote-btn"
              className="flex items-center gap-2 px-6 py-3 bg-sage text-white rounded-full hover:bg-sage-dark transition-colors"
            >
              <Download className="w-4 h-4" />
              Download Image
            </button>
            <button
              onClick={copyToClipboard}
              data-testid="copy-quote-btn"
              className="flex items-center gap-2 px-6 py-3 bg-white text-sage border border-sage/20 rounded-full hover:bg-sage/5 transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Copy Text
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
